import { Button } from "../ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "../ui/dialog";
import {
  Phone,
  Mail,
  MapPin,
  Calendar,
  DollarSign,
  Clock,
  Trash2,
  Home,
  Building2,
  Wrench,
} from "lucide-react";

const projectTypeIcons = {
  residential: Home,
  commercial: Building2,
  renovation: Wrench,
};

export const QuoteDetailDialog = ({ quote, open, onOpenChange, onStatusChange, onDelete }) => {
  if (!quote) return null;

  const address = [quote.address, quote.city, quote.state, quote.zip_code]
    .filter(Boolean)
    .join(", ");

  const Icon = projectTypeIcons[quote.project_type] || Home;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-heading text-2xl font-bold uppercase">
            Quote Request Details
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-6">
          {/* Contact Info */}
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-1">
              <p className="text-xs text-gray-500 uppercase">Name</p>
              <p className="font-medium">{quote.full_name}</p>
            </div>
            <div className="space-y-1">
              <p className="text-xs text-gray-500 uppercase">Email</p>
              <a href={`mailto:${quote.email}`} className="font-medium text-brand-orange hover:underline flex items-center gap-1">
                <Mail className="w-4 h-4" />{quote.email}
              </a>
            </div>
            <div className="space-y-1">
              <p className="text-xs text-gray-500 uppercase">Phone</p>
              <a href={`tel:${quote.phone}`} className="font-medium text-brand-orange hover:underline flex items-center gap-1">
                <Phone className="w-4 h-4" />{quote.phone}
              </a>
            </div>
            <div className="space-y-1">
              <p className="text-xs text-gray-500 uppercase">Preferred Contact</p>
              <p className="font-medium capitalize">{quote.preferred_contact}</p>
            </div>
          </div>

          {/* Address */}
          {address && (
            <div className="space-y-1">
              <p className="text-xs text-gray-500 uppercase">Project Address</p>
              <p className="font-medium flex items-center gap-1">
                <MapPin className="w-4 h-4 text-gray-400" />{address}
              </p>
            </div>
          )}

          {/* Project Details */}
          <div className="grid grid-cols-2 gap-4 p-4 bg-gray-50 rounded-lg">
            <div className="space-y-1">
              <p className="text-xs text-gray-500 uppercase">Project Type</p>
              <p className="font-medium capitalize flex items-center gap-2">
                <Icon className="w-4 h-4" />{quote.project_type}
              </p>
            </div>
            <div className="space-y-1">
              <p className="text-xs text-gray-500 uppercase">Budget Range</p>
              <p className="font-medium flex items-center gap-2">
                <DollarSign className="w-4 h-4" />{quote.budget_range || "Not specified"}
              </p>
            </div>
            <div className="space-y-1">
              <p className="text-xs text-gray-500 uppercase">Timeline</p>
              <p className="font-medium flex items-center gap-2">
                <Clock className="w-4 h-4" />{quote.timeline || "Not specified"}
              </p>
            </div>
            <div className="space-y-1">
              <p className="text-xs text-gray-500 uppercase">Property Size</p>
              <p className="font-medium">{quote.property_size || "Not specified"}</p>
            </div>
          </div>

          {/* Description */}
          <div className="space-y-1">
            <p className="text-xs text-gray-500 uppercase">Project Description</p>
            <p className="text-gray-700 whitespace-pre-wrap">{quote.project_description}</p>
          </div>

          {/* Status & Date */}
          <div className="flex items-center justify-between pt-4 border-t">
            <Select
              value={quote.status || "new"}
              onValueChange={(value) => onStatusChange(quote.id, value)}
            >
              <SelectTrigger className="w-40"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="new">New</SelectItem>
                <SelectItem value="contacted">Contacted</SelectItem>
                <SelectItem value="in_progress">In Progress</SelectItem>
                <SelectItem value="quoted">Quoted</SelectItem>
                <SelectItem value="won">Won</SelectItem>
                <SelectItem value="lost">Lost</SelectItem>
              </SelectContent>
            </Select>
            <div className="text-sm text-gray-500 flex items-center gap-1">
              <Calendar className="w-4 h-4" />
              Submitted {new Date(quote.created_at).toLocaleString()}
            </div>
          </div>

          {/* Actions */}
          <div className="flex gap-3">
            <Button className="flex-1 bg-brand-orange text-white" onClick={() => window.open(`mailto:${quote.email}`)}>
              <Mail className="w-4 h-4 mr-2" />Send Email
            </Button>
            <Button variant="outline" className="flex-1" onClick={() => window.open(`tel:${quote.phone}`)}>
              <Phone className="w-4 h-4 mr-2" />Call
            </Button>
            <Button variant="outline" className="text-red-600 border-red-200 hover:bg-red-50" onClick={() => onDelete(quote.id)}>
              <Trash2 className="w-4 h-4" />
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};
